import React, { useEffect, useState } from 'react';
import { Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { fmtRelative, domainOf, youtubeThumb } from '../lib/utils';
import { getPublicCollection } from '../lib/api';
import { sourceMeta } from '../lib/sourceMeta';
import { useLanguage } from '../lib/LanguageContext';

/** Public, read-only view of several shared notes at once. No login required. */
export default function PublicCollectionView({ owner, ids }) {
  const { t, language } = useLanguage();
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getPublicCollection(owner, ids);
        if (!cancelled) setDocs(r.documents || []);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? t('publicView.notesUnavailable')
              : e?.response?.data?.detail || e.message || t('publicView.notesLoadFailed'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, ids]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-keep-page px-4">
        <div className="flex flex-col items-center gap-3 text-center" data-testid="public-collection-error">
          <AlertTriangle className="w-8 h-8 text-keep-textMuted" />
          <p className="text-[15px] text-keep-text">{error}</p>
          <a href="/" className="text-[13px] text-keep-urlBlue hover:underline">
            {t('publicView.goToApp')}
          </a>
        </div>
      </div>
    );
  }

  if (!docs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-keep-page">
        <Loader2 className="w-8 h-8 text-keep-accent animate-spin" data-testid="public-collection-loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-keep-page">
      <header className="border-b border-keep-border bg-keep-surface px-4 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <img src="/logo192.png" alt="Recycle Brain" className="w-7 h-7 rounded-lg" />
          <span className="text-[15px] font-medium text-keep-text">Recycle Brain</span>
        </a>
        <span className="text-[11px] text-keep-textFaint uppercase tracking-wide">
          {t('publicView.sharedNotes', { n: docs.length, s: docs.length === 1 ? '' : 's' })}
        </span>
      </header>

      <main className="max-w-4xl mx-auto px-3 md:px-4 py-6 md:py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="public-collection-grid">
          {docs.map((doc) => {
            const { Icon, color, labelKey } = sourceMeta(doc.source_type);
            const isText = doc.source_type === 'text';
            const bodyText = isText ? doc.body_text || doc.summary || '' : doc.reason || '';
            const ytThumb = doc.source_type === 'youtube' ? doc.preview?.image || youtubeThumb(doc.source_url) : '';
            const heroImage = doc.source_type === 'youtube' ? ytThumb : doc.preview?.image;

            return (
              <a
                key={doc.id}
                href={`/n/${encodeURIComponent(owner)}/${encodeURIComponent(doc.id)}`}
                className="bg-keep-surface border border-keep-border rounded-lg shadow-keep hover:shadow-lg transition-shadow overflow-hidden"
                data-testid="public-collection-card"
              >
                {heroImage && (
                  <img
                    src={heroImage}
                    alt={doc.title || ''}
                    className="w-full h-32 object-cover border-b border-keep-border"
                    loading="lazy"
                  />
                )}
                <div className="p-3.5">
                  <div className="flex items-center gap-1.5 mb-1.5 text-[11px] uppercase tracking-wide">
                    <Icon className={`w-3.5 h-3.5 ${color}`} />
                    <span className="text-keep-textMuted">{t(labelKey)}</span>
                    {doc.created_at && (
                      <span className="text-keep-textFaint ml-auto">{fmtRelative(doc.created_at, t, language)}</span>
                    )}
                  </div>
                  {doc.title && (
                    <h3 className="text-[15px] font-medium leading-snug text-keep-text mb-1 line-clamp-2 break-words">
                      {doc.title}
                    </h3>
                  )}
                  {bodyText && (
                    <p className="text-[13px] text-keep-text/90 leading-snug line-clamp-4 whitespace-pre-wrap break-words">
                      {bodyText}
                    </p>
                  )}
                  {doc.source_url && doc.source_type !== 'pdf' && (
                    <div className="mt-2 flex items-center gap-1 text-[11px] text-keep-urlBlue truncate">
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{domainOf(doc.source_url) || doc.source_url}</span>
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        </div>

        <div
          className="mt-6 bg-keep-surface border border-keep-border rounded-lg p-5 flex flex-col items-center text-center gap-2"
          data-testid="public-collection-cta"
        >
          <img src="/logo192.png" alt="Recycle Brain" className="w-9 h-9 rounded-lg mb-1" />
          <p className="text-[15px] font-medium text-keep-text">{t('publicView.ctaTitle')}</p>
          <p className="text-[13px] text-keep-textMuted max-w-sm">{t('publicView.ctaBody')}</p>
          <a
            href="/"
            className="mt-2 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-medium bg-keep-accent text-keep-onAccent hover:opacity-90 transition-colors"
            data-testid="public-collection-cta-login"
          >
            {t('publicView.ctaButton')}
          </a>
        </div>
      </main>
    </div>
  );
}
